import {
  type AddMultiJpdRequest,
  type BucketLicenseKeysRequest,
  type BucketSignedUrlResponse,
  type ChangeProviderRequest,
  type ChangeRegionRequest,
  type CreateSubscriptionUserRequest,
  type DebtPaymentFlowRequest,
  DeploymentType,
  type DowngradeSubscriptionRequest,
  type EditSubscriptionUserRequest,
  type EffectiveCancelSubscriptionRequest,
  type EnterprisePlusAddServersRequest,
  type GeoIpCountries,
  isRestClientError,
  type JpuDTO,
  type LicenseKeysRequest,
  type LicenseKeysResponse,
  type LogShippingEnrollmentRequest,
  type ManageAccountAiStateRequest,
  type ManageAccountAiStateResponse,
  type MfaCancelRequest,
  type MfaOtpRequest,
  type MfaStartEnrollmentResponse,
  type PaymentType,
  type PlatformSsoAccessRequest,
  type PurchaseCloudRequest,
  ReadMultipleWhiteListIPsRequest,
  type ReadServerIPMetaResponse,
  type ReadServerStatusResponse,
  type RegionDTO,
  type RegionsListResponse,
  type RequestCurationTrialRequest,
  type RequestJasTrialRequest,
  type ServerStatusRequest,
  type SubscriptionActionRequest,
  type SubscriptionDTO,
  type SubscriptionResponse,
  type SubscriptionsResponse,
  type UpdateEndpointDescriptionRequest,
  type UpdateGeoIPCountriesRequest,
  type UpdateWhiteListIPsRequest,
  type UpgradeSubscriptionRequest,
  UserDTO,
  UserRoleLightDto,
  type WhiteListIPSResponse,
} from "@shared/types/common";
import { type GetOwnersResponse } from "@shared/types/common/owners";
import { useSubscriptionsStore } from "@shared/stores/subscriptions";
import apiProxy from "@shared/utils/api-proxy";
import {
  type ManageEndpointRequestMjf,
  type Region,
} from "@shared/types/localtypes";
import { cookiesService } from "@shared/services/cookies";
import { addSubscriptionMeta } from "@shared/services/metas/subscriptionMetas";
import { offeringsService } from "@shared/services/offerings";
import { notificationsCenterService } from "@shared/services/notificationsCenter.ts";
import { useEventLogger } from "@shared/use/useEventLogger.ts";
import { EventEntity, EventType } from "@shared/types/localTypes/events.ts";
import { usersService } from "@shared/services/user.ts";
import { authorizationService } from "@shared/services/authorization.ts";
import { useAuthStore } from "@shared/stores/auth.ts";
import { useBootstrapStore } from "@shared/stores/bootstrap.ts";
import { GetAQuoteRequestV2 } from "@shared/types/localTypes/marketo.ts";
import { useUpgradeStore } from "@shared/stores/upgrade.ts";
import dateUtils from "@shared/utils/date-utils.ts";
import { DateTime } from "luxon";
import { RouteNames } from "@shared/types/localTypes/router.ts";
import { appService } from "@shared/services/app.ts";
import { router } from "@shared/module-transformers/router.ts";
import { useLoginMessage } from "@shared/use/useLoginMessage.ts";
import useInAppNotificationsPubSub from "@shared/use/in-app-notifications/useInAppNotificationsPubSub.ts";
import { inAppNotificationsService } from "@shared/services/inAppNotifications.ts";
import logger from "@shared/utils/logger.ts";
import { jpdUtils } from "@shared/utils/jpd-utils.ts";

export type InitialData = {
  subscriptions: SubscriptionDTO[];
  user: UserDTO | null;
};

export const subscriptionsService = {
  fetchSubscriptions: async (): Promise<SubscriptionDTO[]> => {
    const response: SubscriptionsResponse | null =
      await apiProxy.get("subscriptions");

    if (!response) {
      throw new Error("Could not load subscriptions");
    }

    return addSubscriptionMeta(response.subscriptions);
  },
  loadInitialData: async (): Promise<void> => {
    const bootstrapStore = useBootstrapStore();
    if (bootstrapStore.initialDataLoaded) {
      return;
    }

    try {
      await Promise.all([
        subscriptionsService.getSubscriptionsAndRelatedDependencies(),
        usersService.getOrFetchUser(),
        authorizationService.getAuthorization(),
      ]);
    } catch (e) {
      if (isRestClientError(e)) {
        if (e.httpStatus === 403) {
          return appService.redirectToForbiddenPage();
        }
        useLoginMessage().setLoginMessage({
          type: "error",
          description:
            "There is an issue with one or more of your subscriptions. Please contact service@jfrog.com",
        });
      }
      logger.error(e);

      await appService.unauthorizedApiInterceptor(router);
      return;
    }

    bootstrapStore.setInitialDataLoaded(true);

    const user = useAuthStore().user;
    if (user) {
      useInAppNotificationsPubSub().startInAppNotificationListening(
        (user as UserDTO).email,
      );
    }
    return;
  },
  getSubscriptionsAndRelatedDependencies: async (): Promise<
    SubscriptionDTO[]
  > => {
    const subscriptions = await subscriptionsService.getSubscriptions();
    // NOTE: [19/12/2023] [kevinz] -> Pricing page is displaying spinner until the offerings are loaded. So here we can safely remove the await of loadOfferings()
    offeringsService.loadOfferings(subscriptions[0].accountNumber);

    // NOTE: [19/12/2023] [kevinz] -> Notification Center is displaying spinner if the notifications are not loaded so we can safely remove the await of loadSmartNotifications()
    notificationsCenterService.loadNotifications();

    await inAppNotificationsService.load2WeeksOldInAppNotifications(
      dateUtils.lastWeekStart().toMillis(),
      DateTime.now().toMillis(),
    );
    return subscriptions;
  },
  getSubscriptions: async (): Promise<SubscriptionDTO[]> => {
    const subscriptionsStore = useSubscriptionsStore();
    const cachedSubscriptions = subscriptionsStore.subscriptions;

    // subscriptions already loaded in Pinia
    if (cachedSubscriptions) {
      return cachedSubscriptions;
    }
    const fetchedSubscriptions =
      await subscriptionsService.fetchSubscriptions();

    await subscriptionsStore.setSubscriptions(fetchedSubscriptions);
    return fetchedSubscriptions;
  },
  findCachedSubscription: (accountNumber: string): SubscriptionDTO | null => {
    if (!accountNumber) {
      return null;
    }
    const subscriptionStore = useSubscriptionsStore();
    return (
      subscriptionStore.subscriptions?.find(
        (s) => s.accountNumber === Number(accountNumber),
      ) || null
    );
  },
  getFallbackRouteNameForSubscription: (accountNumber: string): RouteNames => {
    const allSubscriptions = useSubscriptionsStore().subscriptions || [];
    const targetSubscription = allSubscriptions.find(
      (s) => s.accountNumber === Number(accountNumber),
    );
    if (!targetSubscription) {
      return RouteNames.Root;
    }
    const meta = targetSubscription.meta;

    return meta.isSaas && !meta.hasServers && meta.canConfigureSomeServers
      ? RouteNames.Topology
      : RouteNames.Dashboard;
  },
  extractProviderName: (provider: JpuDTO["cloudProvider"]): string => {
    switch (provider) {
      case "amazon":
        return "Amazon Web Services";
      case "google":
        return "Google Cloud Platform";
      case "azure":
        return "Microsoft Azure";
      default:
        return "-";
    }
  },
  paymentMethod: (subscription: SubscriptionDTO | null): string | null => {
    if (!subscription) {
      return null;
    }

    const { meta } = subscription;

    if (meta.isBilled) {
      if (meta.isPrepaidPayment) {
        return "Annual";
      }

      if (meta.isMonthlyPayment) {
        return "Monthly";
      }
    }

    if (meta.isMP) {
      const marketplace = meta.isAWSMP
        ? "Amazon Web Services"
        : meta.isAzureMP
          ? "Microsoft Azure"
          : "Google Cloud Platform";
      return `Via ${marketplace}`;
    }

    return null;
  },

  changeProvider: async (
    changeProviderRequest: ChangeProviderRequest,
  ): Promise<null> => {
    return await apiProxy.post(
      "subscriptions/changeProvider",
      changeProviderRequest,
    );
  },

  getCertificates: async (
    accountNumber: SubscriptionDTO["accountNumber"],
  ): Promise<string> => {
    const response: string | null = await apiProxy.get(
      `subscriptions/certificates/${accountNumber}`,
      {
        responseType: "blob",
      },
    );

    if (!response) {
      throw new Error("Could not load certificates file");
    }
    return response;
  },

  changeRegion: async (
    changeRegionRequest: ChangeRegionRequest,
  ): Promise<null> => {
    return await apiProxy.post(
      `subscriptions/changeRegion`,
      changeRegionRequest,
    );
  },

  getRegionsList: async (): Promise<Region[]> => {
    const response: RegionsListResponse | null = await apiProxy.get(
      "subscriptions/regionsList",
    );
    if (!response) {
      throw new Error("Could not load regions list");
    }
    return transformRegions(response.regions);
  },
  purchaseCloud: async (
    request: PurchaseCloudRequest,
  ): Promise<SubscriptionDTO> => {
    const response: SubscriptionResponse | null = await apiProxy.post(
      "subscriptions/purchaseCloud",
      request,
    );

    if (!response) {
      throw new Error("Could not finalize Cloud Purchase");
    }

    return addSubscriptionMeta([response.subscription])[0];
  },
  upgradeCloud: async (
    request: UpgradeSubscriptionRequest,
  ): Promise<SubscriptionDTO> => {
    const response: SubscriptionResponse | null = await apiProxy.post(
      "subscriptions/upgrade",
      request,
    );

    if (!response) {
      throw new Error("Could not finalize Cloud Upgrade");
    }

    return addSubscriptionMeta([response.subscription])[0];
  },
  downgradeSubscription: async (
    accountNumber: string,
    reason: string,
    newPaymentType: PaymentType,
  ): Promise<SubscriptionDTO> => {
    const request: { reason: string; newPaymentType: PaymentType } = {
      reason,
      newPaymentType,
    };
    const response: SubscriptionResponse | null = await apiProxy.post(
      `subscriptions/${accountNumber}/downgrade`,
      request,
    );
    if (!response) {
      throw new Error(`Could not downgrade subscription '${accountNumber}'`);
    }
    return addSubscriptionMeta([response.subscription])[0];
  },

  sendDowngradeRequest: async (serverName: string): Promise<null> => {
    return await apiProxy.post(`subscriptions/downgrade/${serverName}`, {
      marketoCookie: cookiesService.marketoCookie(),
    } as DowngradeSubscriptionRequest);
  },

  sendSubscriptionActionRequest: async (
    request: SubscriptionActionRequest,
  ): Promise<void> => {
    await apiProxy.post(`subscriptions/requestAction`, request);
  },

  cancelSubscription: async (
    accountNumber: SubscriptionDTO["accountNumber"],
    serverName: JpuDTO["serverName"],
    isJCR: boolean,
    reason: string,
  ): Promise<SubscriptionDTO> => {
    const request: EffectiveCancelSubscriptionRequest = {
      reason,
      serverName,
      isJCR,
      marketoCookie: cookiesService.marketoCookie() || null,
    };
    const response: SubscriptionResponse | null = await apiProxy.post(
      `subscriptions/${accountNumber}/cancel`,
      request,
    );
    if (!response) {
      throw new Error(`Could not cancel subscription '${accountNumber}'`);
    }
    return addSubscriptionMeta([response.subscription])[0];
  },

  startMfaEnrollment: async (
    accountNumber: SubscriptionDTO["accountNumber"],
  ): Promise<MfaStartEnrollmentResponse> => {
    const response: MfaStartEnrollmentResponse | null = await apiProxy.post(
      `subscriptions/${accountNumber}/mfa/enrollment/start`,
      {},
    );
    if (!response) {
      throw new Error("Could not start enrollment");
    }
    return response;
  },

  completeMfaEnrollment: async (
    accountNumber: SubscriptionDTO["accountNumber"],
    request: MfaOtpRequest,
  ) => {
    return await apiProxy.post(
      `subscriptions/${accountNumber}/mfa/enrollment/complete`,
      request,
    );
  },

  cancelMfaEnrollment: async (request: MfaCancelRequest) => {
    return await apiProxy.post(`subscriptions/mfa/enrollment/cancel`, request);
  },

  extractMainSaasServer: (subscription: SubscriptionDTO): JpuDTO | null => {
    const { jpus } = subscription;
    if (!jpus || !jpus.length) {
      return null;
    }
    const firstActiveMothership = jpus.find(
      (jpu) => jpu.status === "ACTIVE" && jpdUtils.isMothership(jpu),
    );
    const firstNotActiveMothership = jpus.find(
      (jpu) => jpu.status !== "ACTIVE" && jpdUtils.isMothership(jpu),
    );

    const firstActive = jpus.find((jpu) => jpu.status === "ACTIVE");

    return (
      firstActiveMothership ||
      firstNotActiveMothership ||
      firstActive ||
      jpus[0]
    );
  },
  filterSubscriptionsForMothership: (
    subscription: SubscriptionDTO,
  ): JpuDTO[] => {
    const { jpus } = subscription;

    const jpuMotherships = jpus
      .filter((jpu) => jpdUtils.isMothership(jpu))
      .sort();
    if (jpuMotherships.length === 0) {
      return jpus;
    }
    return jpuMotherships;
  },
  addServersToTopology: async (
    serversRequest: EnterprisePlusAddServersRequest,
  ) => {
    const response: SubscriptionResponse | null = await apiProxy.post(
      `subscriptions/topology/addServers`,
      serversRequest,
    );
    if (!response) {
      throw new Error(`Could not add servers to topology`);
    }
    return addSubscriptionMeta([response.subscription])[0];
  },

  addJpds: async (
    accountNumber: string,
    serversRequest: AddMultiJpdRequest,
  ) => {
    const response: SubscriptionResponse | null = await apiProxy.put(
      `subscriptions/${accountNumber}/addJpd`,
      serversRequest,
    );
    if (!response) {
      throw new Error(`Could not add servers to topology`);
    }
    return addSubscriptionMeta([response.subscription])[0];
  },
  getServersStatusParallel: async (
    accountNumber: string,
    request: ServerStatusRequest,
  ): Promise<ReadServerStatusResponse> => {
    const readServerStatusResponses = await Promise.all(
      request.technicalServerNames.map(async (techName) =>
        subscriptionsService.getServersStatus(accountNumber, {
          technicalServerNames: [techName],
        }),
      ),
    );

    return readServerStatusResponses.reduce(
      (previousValue, currentValue) => ({ ...previousValue, ...currentValue }),
      {} as ReadServerStatusResponse,
    );
  },
  getServersStatus: async (
    accountNumber: string,
    request: ServerStatusRequest,
  ): Promise<ReadServerStatusResponse> => {
    const response: ReadServerStatusResponse | null = await apiProxy.post(
      `subscriptions/${accountNumber}/getServersStatus`,
      request,
    );
    if (!response) {
      throw new Error("Could not load servers status");
    }
    return response;
  },
  createSubscriptionUser: async (
    accountNumber: SubscriptionDTO["accountNumber"],
    request: CreateSubscriptionUserRequest,
  ): Promise<UserRoleLightDto> => {
    const response: UserRoleLightDto | null = await apiProxy.post(
      `subscriptions/${accountNumber}/users`,
      request,
    );
    if (!response) {
      throw new Error("An error occurred...");
    }
    return response;
  },
  editSubscriptionUser: async (
    accountNumber: SubscriptionDTO["accountNumber"],
    userIdentifier: string,
    request: EditSubscriptionUserRequest,
  ): Promise<UserRoleLightDto> => {
    const response: UserRoleLightDto | null = await apiProxy.put(
      `subscriptions/${accountNumber}/users/${userIdentifier}`,
      request,
    );
    if (!response) {
      throw new Error("An error occurred...");
    }

    return response;
  },
  deleteSubscriptionUser: async (
    accountNumber: SubscriptionDTO["accountNumber"],
    userIdentifier: string,
  ): Promise<void> => {
    await apiProxy.delete(
      `subscriptions/${accountNumber}/users/${userIdentifier}`,
    );
  },

  reinviteSubscriptionUser: async (
    accountNumber: SubscriptionDTO["accountNumber"],
    userIdentifier: string,
  ) => {
    await apiProxy.post(
      `subscriptions/${accountNumber}/users/${userIdentifier}/reinvite`,
      {},
    );
  },
  getServersParallelMetaData: async (
    accountNumber: string,
    request: ServerStatusRequest,
  ): Promise<ReadServerIPMetaResponse> => {
    const readServerStatusResponses = await Promise.all(
      request.technicalServerNames.map(async (technicalServerName) =>
        subscriptionsService.getServerMetaData(
          accountNumber,
          technicalServerName,
        ),
      ),
    );

    return readServerStatusResponses.reduce(
      (previousValue, currentValue) => ({
        ...previousValue,
        ...currentValue,
      }),
      {} as ReadServerIPMetaResponse,
    );
  },

  getServerMetaData: async (
    accountNumber: string,
    technicalServerName: string,
  ): Promise<ReadServerIPMetaResponse> => {
    const response: ReadServerIPMetaResponse | null = await apiProxy.post(
      `subscriptions/${accountNumber}/getServersMetaData`,
      {
        technicalServerNames: [technicalServerName],
      } satisfies ReadMultipleWhiteListIPsRequest,
    );
    if (!response) {
      throw new Error("Could not load server Ip Meta data");
    }

    const firstKey = Object.keys(response)[0];

    response[firstKey].geoIPMeta["geoIpRestrictionCountriesList"] =
      response[firstKey].geoIPMeta.geoIpRestrictionCountriesList || [];
    response[firstKey].geoIPMeta["geoIpRestrictionType"] =
      response[firstKey].geoIPMeta.geoIpRestrictionType || "deny";

    // NOTE: [16/02/2025] [kevinz] -> we need to build the response key ourself because Store will return a
    //  bad server name key in case of 'wsanity' server (will return a hard coded 'stam' as name)
    return {
      [technicalServerName]: response[firstKey],
    } satisfies ReadServerIPMetaResponse;
  },

  updateWhiteListIPs: async (
    accountNumber: string,
    serverName: JpuDTO["serverName"],
    request: UpdateWhiteListIPsRequest,
  ): Promise<WhiteListIPSResponse> => {
    const whiteListIPSResponse: WhiteListIPSResponse | null =
      await apiProxy.put(
        `subscriptions/${accountNumber}/servers/${serverName}/whiteListIPs`,
        request,
      );
    if (!whiteListIPSResponse) {
      throw new Error("Could not update Whitelist IPs");
    }
    return whiteListIPSResponse;
  },

  updateEndpointDescription: async (
    accountNumber: string,
    payload: UpdateEndpointDescriptionRequest,
  ): Promise<void> => {
    await apiProxy.post(
      `subscriptions/${accountNumber}/updatePrivateEndpointDescription`,
      payload,
    );
  },

  manageEndpoint: async (
    accountNumber: string,
    request: ManageEndpointRequestMjf,
  ) => {
    const manageEndpointResponse: boolean | null = await apiProxy.post(
      `subscriptions/${accountNumber}/managePrivateEndpoint`,
      request,
    );
    if (!manageEndpointResponse) {
      throw new Error("Could not update Endpoint");
    }
    return manageEndpointResponse;
  },

  updateGeoIPCountries: async (
    accountNumber: string,
    serverName: JpuDTO["serverName"],
    request: UpdateGeoIPCountriesRequest,
  ): Promise<GeoIpCountries> => {
    const geoIpCountriesResponse: GeoIpCountries | null = await apiProxy.put(
      `subscriptions/${accountNumber}/servers/${serverName}/geoIpCountries`,
      request,
    );
    if (!geoIpCountriesResponse) {
      throw new Error("Could not update geoIp Countries");
    }
    return geoIpCountriesResponse;
  },

  logShippingEnrollment: async (request: LogShippingEnrollmentRequest) => {
    await apiProxy.post(`subscriptions/logShippingEnrollment`, request);
  },

  changePlatformAccessForSubscription: async (
    accountNumber: SubscriptionDTO["accountNumber"],
    request: PlatformSsoAccessRequest,
  ): Promise<null> => {
    return await apiProxy.put(
      `subscriptions/${accountNumber}/platformSsoAccess`,
      request,
    );
  },

  debtPaymentFlow: async (
    accountNumber: string,
    request: DebtPaymentFlowRequest,
  ): Promise<null> => {
    return await apiProxy.post(
      `subscriptions/${accountNumber}/debtPaymentFlow`,
      request,
    );
  },

  generateSignedUrl: async (
    accountNumber: SubscriptionDTO["accountNumber"],
    bucketNumber: string,
  ): Promise<BucketSignedUrlResponse> => {
    const response: BucketSignedUrlResponse | null = await apiProxy.get(
      `subscriptions/${accountNumber}/buckets/${bucketNumber}/generateSignedUrl`,
    );
    if (!response) {
      throw new Error("Could not generate signed URL");
    }
    return response;
  },

  fetchByEntitlements: async (
    accountNumber: SubscriptionDTO["accountNumber"],
    request: LicenseKeysRequest,
  ): Promise<LicenseKeysResponse> => {
    const response: LicenseKeysResponse | null = await apiProxy.post(
      `subscriptions/${accountNumber}/licensing/fetchByEntitlements`,
      request,
    );
    if (!response) {
      throw new Error("Error fetching license keys");
    }
    return response;
  },

  fetchByBuckets: async (
    accountNumber: SubscriptionDTO["accountNumber"],
    request: BucketLicenseKeysRequest,
  ): Promise<LicenseKeysResponse> => {
    const response: LicenseKeysResponse | null = await apiProxy.post(
      `subscriptions/${accountNumber}/licensing/fetchByBuckets`,
      request,
    );
    if (!response) {
      throw new Error("Error fetching bucket license keys");
    }
    return response;
  },

  requestAJasTrial: async (request: RequestJasTrialRequest): Promise<void> => {
    await apiProxy.post("subscriptions/request-jas-trial", request);

    useEventLogger().logEvent({
      eventType: EventType.REQUEST_TRIAL_JAS,
      eventEntity: EventEntity.PLAN,
      eventData: request as unknown as Record<string, unknown>,
    });
  },
  requestCurationTrial: async (
    request: RequestCurationTrialRequest,
  ): Promise<void> => {
    await apiProxy.post("subscriptions/request-curation-trial", request);

    useEventLogger().logEvent({
      eventType: EventType.REQUEST_TRIAL_CURATION,
      eventEntity: EventEntity.PLAN,
      eventData: request as unknown as Record<string, unknown>,
    });
  },

  isSelfHosted: (subscription: SubscriptionDTO) => {
    const registrationGroups = subscription.registrationGroups;
    if (!registrationGroups.length) {
      return subscription.paymentType === "BOM_BASED";
    }
    return registrationGroups[0].deploymentType === DeploymentType.SELF_HOSTED;
  },
  isSaas: (subscription: SubscriptionDTO) => {
    return !subscriptionsService.isSelfHosted(subscription);
  },
  isHybridPaymentType: (paymentType: PaymentType | undefined): boolean => {
    if (!paymentType) return false;
    return (
      paymentType === "ENTERPRISE_PLUS_HYBRID" ||
      paymentType === "TRIAL_ENTERPRISE_PLUS_HYBRID"
    );
  },
  sendGetAQuote: async (request: GetAQuoteRequestV2) => {
    await apiProxy.post(`subscriptions/getaquote/v2`, request);

    useEventLogger().logEvent({
      eventType: useUpgradeStore().isQuickUpgrade
        ? EventType.QUICK_GET_QUOTE
        : EventType.GET_QUOTE,
      eventEntity: EventEntity.PLAN,
      eventData: request as unknown as Record<string, unknown>,
    });
  },

  fetchOwners: async (
    accountNumber: number,
    first: number,
    max: number,
    search?: string,
  ): Promise<GetOwnersResponse> => {
    const params = new URLSearchParams();
    params.set("first", String(first));
    params.set("max", String(max));
    if (search) {
      params.set("search", search);
    }
    const queryString = params.toString();
    const url = `subscriptions/${accountNumber}/owners?${queryString}`;
    const response = await apiProxy.get<GetOwnersResponse>(url);
    if (!response) {
      throw new Error("Failed to fetch owners");
    }
    return response;
  },

  manageAccountAiState: async (
    request: ManageAccountAiStateRequest,
  ): Promise<ManageAccountAiStateResponse> => {
    const response: ManageAccountAiStateResponse | null = await apiProxy.post(
      "subscriptions/manageAccountAiState",
      request,
    );
    if (!response) {
      throw new Error("Could not manage account AI state");
    }
    return response;
  },
};

const transformRegions = (regions: RegionDTO[]): Region[] => {
  return regions.map((region) => {
    return {
      ...region,
      jpuProviderCode: extractJPUProviderCodeFromRegion(region.providerCode),
    };
  });
};

const extractJPUProviderCodeFromRegion = (
  regionProviderCode: RegionDTO["providerCode"],
): JpuDTO["cloudProvider"] => {
  switch (regionProviderCode) {
    case "AWS":
      return "amazon";
    case "AZURE":
      return "azure";
    case "GCP":
      return "google";
  }
};
